import { ActionIcon, Checkbox, Group, Loader, Stack, Text, Textarea, TextInput, Tooltip } from "@mantine/core";
import { useHover } from "@mantine/hooks";
import { IIssueTodoItem } from "@schemas/issueSchema";
import React, { useMemo, useState } from "react";
import { formatDateYmdDots } from "../../app/routes/admin/selectors";
import { IQueryResponse } from "../../frontendTypes";

function formatCompletedTooltip(todo: IIssueTodoItem) {
  if (!todo.completedDate) return null;
  const dateStr = formatDateYmdDots(todo.completedDate);
  const by = todo.completedByName ?? "";
  return (
    <Stack gap={0} align="center">
      <Text size="xs">{by}</Text>
      <Text size="xs">{dateStr}</Text>
    </Stack>
  );
}

interface TodoListProps<T> {
  todos: IIssueTodoItem[];
  updateTodo: (_id: string, value: string) => IQueryResponse<T> | Promise<void>;
  addNewTodo: (text: string) => IQueryResponse<T> | Promise<void>; // Todo: remove promise<void> after old code is gone
  deleteTodo: (todoId: string) => IQueryResponse<T> | Promise<void>;
  toggleTodo: (todoId: string, isChecked: boolean) => IQueryResponse<T> | Promise<void>;
  reorderTodos?: (todoIds: string[]) => IQueryResponse<T> | Promise<void>;
}

function TodoList<T>({ todos, updateTodo, addNewTodo, deleteTodo, toggleTodo, reorderTodos }: TodoListProps<T>) {
  const sortedTodos = useMemo(
    () => [...(todos ?? [])].sort((a, b) => (a.listIndex ?? 0) - (b.listIndex ?? 0)),
    [todos],
  );

  const moveTodo = async (todoId: string, direction: -1 | 1) => {
    if (reorderTodos == null) return;

    const currentIndex = sortedTodos.findIndex((todo) => String(todo._id) === todoId);
    const nextIndex = currentIndex + direction;

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= sortedTodos.length) {
      return;
    }

    const nextTodos = [...sortedTodos];
    const [movedTodo] = nextTodos.splice(currentIndex, 1);
    nextTodos.splice(nextIndex, 0, movedTodo);

    await reorderTodos(nextTodos.map((todo) => String(todo._id)));
  };

  return (
    <Stack gap={0} flex={1}>
      <Stack gap={0}>
        {sortedTodos.map((todo, index) => (
          <TodoItem
            key={String(todo._id)}
            todo={todo}
            canMoveUp={index > 0}
            canMoveDown={index < sortedTodos.length - 1}
            updateTodo={updateTodo}
            deleteTodo={deleteTodo}
            toggleTodo={toggleTodo}
            moveTodo={reorderTodos != null ? moveTodo : undefined}
          />
        ))}
      </Stack>

      <AddTodoItem addTodo={addNewTodo} />
    </Stack>
  );
}

interface TodoItemProps<T> {
  todo: IIssueTodoItem;
  canMoveUp: boolean;
  canMoveDown: boolean;
  toggleTodo: (todoId: string, isChecked: boolean) => IQueryResponse<T> | Promise<void>;
  updateTodo: (_id: string, value: string) => IQueryResponse<T> | Promise<void>;
  deleteTodo: (todoId: string) => IQueryResponse<T> | Promise<void>;
  moveTodo?: (todoId: string, direction: -1 | 1) => Promise<void>;
}

function TodoItem<T>({ todo, canMoveUp, canMoveDown, updateTodo, deleteTodo, toggleTodo, moveTodo }: TodoItemProps<T>) {
  const { hovered, ref } = useHover();
  const [value, setValue] = useState<string>(todo.text ?? "");
  const [loading, setLoading] = useState<boolean>(false);

  const saveChange = async () => {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === (todo.text.trim() ?? "")) {
      setValue(todo.text.trim() ?? "");
    } else {
      try {
        setLoading(true);
        await updateTodo(String(todo._id), trimmed);
      } finally {
        setLoading(false);
      }
    }
  };

  const onInputKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await saveChange();
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setValue(todo.text ?? "");
    }
  };

  const completed = todo.completedDate != null;
  const tooltipContent = formatCompletedTooltip(todo);

  const stopEscOnKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      return;
    }
  };

  return (
    <Group ref={ref} wrap="nowrap" align="center" gap="xs">
      <Tooltip label={tooltipContent} disabled={!completed} withArrow zIndex={2000}>
        <Checkbox
          aria-label={completed ? "Fjern fullført" : "Marker som fullført"}
          checked={completed}
          onChange={async () => {
            try {
              setLoading(true);
              await toggleTodo(String(todo._id), completed);
            } finally {
              setLoading(false);
            }
          }}
        />
      </Tooltip>

      <Textarea
        flex={1}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={onInputKeyDown}
        onKeyUp={stopEscOnKeyUp}
        onBlur={saveChange}
        variant="unstyled"
        readOnly={completed || loading}
        rightSection={loading ? <Loader size="sm" /> : null}
        autosize
        minRows={1}
        maxRows={4}
        styles={{
          input: {
            border: "0",
            textDecoration: completed ? "line-through" : "none",
            color: completed ? "var(--mantine-color-dimmed)" : "inherit",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          },
        }}
      />

      {moveTodo != null && (
        <Group gap={0} wrap="nowrap">
          <ActionIcon
            disabled={loading || !canMoveUp}
            variant="subtle"
            aria-label="Flytt opp"
            onClick={async () => {
              try {
                setLoading(true);
                await moveTodo(String(todo._id), -1);
              } finally {
                setLoading(false);
              }
            }}
            style={{
              opacity: hovered ? 1 : 0,
              transition: "opacity 200ms ease",
              pointerEvents: hovered ? "auto" : "none",
            }}
          >
            <i className="fa fa-arrow-up fa-fw" />
          </ActionIcon>
          <ActionIcon
            disabled={loading || !canMoveDown}
            variant="subtle"
            aria-label="Flytt ned"
            onClick={async () => {
              try {
                setLoading(true);
                await moveTodo(String(todo._id), 1);
              } finally {
                setLoading(false);
              }
            }}
            style={{
              opacity: hovered ? 1 : 0,
              transition: "opacity 200ms ease",
              pointerEvents: hovered ? "auto" : "none",
            }}
          >
            <i className="fa fa-arrow-down fa-fw" />
          </ActionIcon>
        </Group>
      )}

      <ActionIcon
        disabled={loading}
        variant="subtle"
        aria-label="Slett"
        onClick={async () => {
          try {
            setLoading(true);
            await deleteTodo(String(todo._id));
          } finally {
            setLoading(false);
          }
        }}
        style={{
          opacity: hovered ? 1 : 0,
          transition: "opacity 200ms ease",
          pointerEvents: hovered ? "auto" : "none",
        }}
      >
        <i className="fa fa-trash fa-fw" />
      </ActionIcon>
    </Group>
  );
}

const AddTodoItem: React.FC<{ addTodo: (value: string) => Promise<unknown> }> = ({ addTodo }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    const trimmed = value.trim();
    if (trimmed) {
      setLoading(true);
      await addTodo(trimmed);
      setLoading(false);

      setValue("");
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") {
      save();
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setValue("");
    }
  };

  return (
    <Group align="center" gap="xs" wrap="nowrap">
      <Checkbox aria-label="Ny oppgave" checked={false} readOnly color="orange" />
      <TextInput
        readOnly={loading}
        rightSection={loading ? <Loader size="sm" /> : null}
        placeholder="Legg til punkt"
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        onBlur={save}
        variant="unstyled"
        style={{ flex: 1 }}
        styles={{
          input: {
            border: 0,
          },
        }}
      />
    </Group>
  );
};

export default TodoList;
